import type { CsBrandKit } from '../../../../../common/models/content-studio.model';
import { resolveFontFamily } from './fonts';
import type { BrandInfo, ColorRole } from './types';

/** Neutral defaults: warm near-black ink, warm paper ground, a quiet bronze accent. */
export const DEFAULT_INK = '#1A1917';
export const DEFAULT_GROUND = '#F4F1EC';
export const DEFAULT_ACCENT = '#8C6E4B';

export const isHexColor = (v?: string | null): v is string => /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test((v || '').trim());

export function parseColorWithAlpha(input: string | undefined | null): { hex: string; alpha: number } {
    if (!input || typeof input !== 'string') return { hex: '#000000', alpha: 1 };
    const s = input.trim();
    const rgba = s.match(/^rgba?\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)(?:\s*,\s*([\d.]+))?\s*\)$/i);
    if (rgba) {
        const toHex = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
        return {
            hex: `#${toHex(Number(rgba[1]))}${toHex(Number(rgba[2]))}${toHex(Number(rgba[3]))}`,
            alpha: rgba[4] !== undefined ? Number(rgba[4]) : 1,
        };
    }
    const hex8 = s.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
    if (hex8) return { hex: `#${hex8[1]}${hex8[2]}${hex8[3]}`.toLowerCase(), alpha: parseInt(hex8[4], 16) / 255 };
    const hex6 = s.match(/^#([0-9a-f]{6})$/i);
    if (hex6) return { hex: s.toLowerCase(), alpha: 1 };
    const hex3 = s.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i);
    if (hex3) return { hex: `#${hex3[1]}${hex3[1]}${hex3[2]}${hex3[2]}${hex3[3]}${hex3[3]}`.toLowerCase(), alpha: 1 };
    if (s.toLowerCase() === 'white') return { hex: '#ffffff', alpha: 1 };
    if (s.toLowerCase() === 'black') return { hex: '#000000', alpha: 1 };
    if (s.toLowerCase() === 'transparent') return { hex: '#000000', alpha: 0 };
    return { hex: '#000000', alpha: 1 };
}

export function rgba(hex: string, alpha: number): string {
    const { hex: h } = parseColorWithAlpha(hex);
    const r = parseInt(h.slice(1, 3), 16);
    const g = parseInt(h.slice(3, 5), 16);
    const b = parseInt(h.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha))})`;
}

export function luminance(color: string): number {
    const { hex } = parseColorWithAlpha(color);
    const ch = (i: number) => {
        const v = parseInt(hex.slice(i, i + 2), 16) / 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * ch(1) + 0.7152 * ch(3) + 0.0722 * ch(5);
}

export function contrast(a: string, b: string): number {
    const la = luminance(a);
    const lb = luminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export const isLight = (color: string) => luminance(color) > 0.5;

/** Text colour that reads on a given fill. */
/** Text colour that reads on a fill: white when it holds up (UI-size type), else the ink. */
export const readableOn = (fill: string, ink = DEFAULT_INK) => (contrast(fill, '#FFFFFF') >= 3.2 ? '#FFFFFF' : contrast(fill, ink) >= 3 ? ink : '#000000');

/**
 * Everything the editor knows about the brand, merged: the brand kit (roles,
 * font, name), the props the workspace passes (logo, colours), and the
 * account's logo. Missing roles fall back to the neutral defaults so a vendor
 * with no kit still gets designs that look finished.
 */
export function resolveBrand(
    kit: CsBrandKit | null | undefined,
    hint: { logoUrl?: string; colors?: string[] } | undefined,
    accountLogo?: string,
): BrandInfo {
    const colors = Array.from(new Set([
        ...(kit?.primaryColors || []),
        ...(kit?.secondaryColors || []),
        ...(hint?.colors || []),
    ].filter(isHexColor).map((c) => c.toLowerCase())));
    const roles = kit?.colorRoles || {};
    const ink = isHexColor(roles.ink) ? roles.ink! : DEFAULT_INK;
    const ground = isHexColor(roles.ground) ? roles.ground! : DEFAULT_GROUND;
    // The accent must be visible on the ground; a near-white "primary colour"
    // from a site's background is not an accent.
    const accentCandidate = isHexColor(roles.accent)
        ? roles.accent!
        : colors.find((c) => contrast(c, ground) >= 2.2 && contrast(c, ink) >= 1.4);
    const accent = accentCandidate || DEFAULT_ACCENT;
    const onAccent = isHexColor(roles.onAccent) ? roles.onAccent! : readableOn(accent, ink);
    const logoUrl = kit?.logoUrl || hint?.logoUrl || accountLogo || undefined;
    const altLogoUrl = accountLogo && accountLogo !== logoUrl ? accountLogo : undefined;
    return {
        name: kit?.brandName || undefined,
        logoUrl,
        altLogoUrl,
        ink,
        ground,
        accent,
        onAccent,
        colors,
        font: kit?.primaryFont ? resolveFontFamily(kit.primaryFont) : undefined,
    };
}

export function colorForRole(brand: BrandInfo, role: ColorRole): string {
    switch (role) {
        case 'ink': return brand.ink;
        case 'ground': return brand.ground;
        case 'accent': return brand.accent;
        case 'onAccent': return brand.onAccent;
        default: return '#FFFFFF';
    }
}

/** The swatches offered in every colour picker: brand first, then a neutral ramp. */
export function swatchesFor(brand: BrandInfo | null): string[] {
    const neutrals = ['#FFFFFF', '#F4F1EC', '#E7E2D9', '#B9B2A6', '#6F6A62', '#3A3833', '#1A1917', '#000000'];
    const brandSet = brand ? [brand.ink, brand.ground, brand.accent, ...brand.colors] : [];
    return Array.from(new Set([...brandSet.map((c) => c.toLowerCase()), ...neutrals.map((c) => c.toLowerCase())])).slice(0, 18);
}

/**
 * A small palette sampled from a picture: k-means-lite over a 40×40 thumbnail.
 * Used by the moodboard template's swatch row. Returns [] if the image is
 * tainted (cross-origin without CORS) — callers fall back to brand colours.
 */
export function extractPalette(img: HTMLImageElement | HTMLCanvasElement, count = 4): string[] {
    try {
        const size = 40;
        const c = document.createElement('canvas');
        c.width = size;
        c.height = size;
        const ctx = c.getContext('2d');
        if (!ctx) return [];
        ctx.drawImage(img as any, 0, 0, size, size);
        const data = ctx.getImageData(0, 0, size, size).data;
        const px: Array<[number, number, number]> = [];
        for (let i = 0; i < data.length; i += 4) px.push([data[i], data[i + 1], data[i + 2]]);
        // Seed centroids spread across the luminance range.
        const sorted = [...px].sort((a, b) => (a[0] + a[1] + a[2]) - (b[0] + b[1] + b[2]));
        let cents = Array.from({ length: count }, (_, k) => sorted[Math.floor(((k + 0.5) / count) * sorted.length)]);
        for (let iter = 0; iter < 8; iter++) {
            const sums = cents.map(() => [0, 0, 0, 0]);
            for (const p of px) {
                let best = 0;
                let bd = Infinity;
                cents.forEach((cc, j) => {
                    const d = (p[0] - cc[0]) ** 2 + (p[1] - cc[1]) ** 2 + (p[2] - cc[2]) ** 2;
                    if (d < bd) { bd = d; best = j; }
                });
                sums[best][0] += p[0]; sums[best][1] += p[1]; sums[best][2] += p[2]; sums[best][3] += 1;
            }
            cents = cents.map((cc, j) => (sums[j][3] ? [sums[j][0] / sums[j][3], sums[j][1] / sums[j][3], sums[j][2] / sums[j][3]] : cc)) as any;
        }
        const toHex = (n: number) => Math.round(n).toString(16).padStart(2, '0');
        return cents
            .sort((a, b) => (a[0] + a[1] + a[2]) - (b[0] + b[1] + b[2]))
            .map((cc) => `#${toHex(cc[0])}${toHex(cc[1])}${toHex(cc[2])}`);
    } catch {
        return [];
    }
}
